import { useState, useEffect, useCallback, useRef } from 'react'
import { db, getFns } from '../../../firebase'
import {
  collection,
  doc,
  addDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  getDocs,
  startAfter,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'

/**
 * Hook for fetching Shopify products from Firestore cache
 * Products are synced automatically every hour via Cloud Function
 */
export function useShopifyProducts() {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastSync, setLastSync] = useState(null)
  const [cursor, setCursor] = useState(null)
  const [hasMore, setHasMore] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const remoteSearchCacheRef = useRef(new Map())
  const pageSize = 200

  const loadPage = useCallback(async (afterDoc = null, replace = false) => {
    setLoading(true)
    setError(null)
    try {
      const constraints = [
        collection(db, 'shopify_products'),
        orderBy('updatedAt', 'asc'),
        firestoreLimit(pageSize)
      ]
      if (afterDoc) {
        constraints.push(startAfter(afterDoc))
      }
      const q = query(...constraints)
      const snapshot = await getDocs(q)
      const prods = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }))
      setProducts(prev => (replace ? prods : [...prev, ...prods]))
      setCursor(snapshot.docs[snapshot.docs.length - 1] || afterDoc)
      setHasMore(snapshot.docs.length === pageSize)
    } catch (err) {
      console.error('Failed to load Shopify products from Firestore:', err)
      if (err.code === 'permission-denied') {
        setError('Please log in to view products.')
      } else {
        setError('Failed to load products. Try refreshing the page.')
      }
    } finally {
      setLoading(false)
    }
  }, [])

  // Initial load
  useEffect(() => {
    loadPage(null, true)
  }, [loadPage])

  // Subscribe to sync metadata
  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'shopify_sync', 'last_sync'),
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data()
          setLastSync({
            at: data.syncedAt?.toDate?.() || null,
            count: data.productCount || 0,
            hasMore: data.hasMore || false
          })
        }
      },
      (err) => {
        // Ignore errors for sync metadata
        console.warn('Could not load sync metadata:', err)
      }
    )

    return () => unsubscribe()
  }, [])

  // Refresh just reloads the page (sync is automatic every hour)
  const refresh = useCallback(() => {
    setCursor(null)
    setHasMore(true)
    remoteSearchCacheRef.current.clear()
    loadPage(null, true)
  }, [loadPage])

  const loadMore = useCallback(() => {
    if (!hasMore || loading) return
    loadPage(cursor, false)
  }, [hasMore, loading, cursor, loadPage])

  const syncMore = useCallback(async () => {
    setSyncing(true)
    try {
      const fn = httpsCallable(getFns(), 'shopifySyncToFirestore')
      await fn({ limit: 50, maxPages: 6, mode: 'full' })
      await refresh()
    } catch (err) {
      console.error('Failed to sync Shopify products:', err)
      const detail = err?.message || err?.details || err?.code || 'Unknown error'
      setError(`Sync failed: ${detail}`)
    } finally {
      setSyncing(false)
    }
  }, [refresh])

  // Update price via Firestore trigger (bypasses CORS)
  const updatePrice = useCallback(async (productId, variantId, price, compareAtPrice) => {
    try {
      // Write to Firestore - Cloud Function will pick it up and update Shopify
      const payload = {
        productId,
        variantId,
        price: parseFloat(price),
        status: 'pending',
        createdAt: serverTimestamp()
      }

      if (compareAtPrice !== undefined) {
        payload.compareAtPrice = compareAtPrice ? parseFloat(compareAtPrice) : null
      }

      await addDoc(collection(db, 'shopify_price_updates'), payload)
      return { ok: true }
    } catch (err) {
      console.error('Failed to request price update:', err)
      throw err
    }
  }, [])

  const ensureBarcode = useCallback(async (productId, variantId, sku) => {
    try {
      const fn = httpsCallable(getFns(), 'shopifyEnsureBarcode')
      const res = await fn({ productId, variantId, sku })
      return res.data
    } catch (err) {
      console.error('Failed to ensure barcode:', err)
      throw err
    }
  }, [])

  const updateInventory = useCallback(async (productId, variantId, quantity) => {
    try {
      const fn = httpsCallable(getFns(), 'shopifyUpdateInventory')
      const res = await fn({ productId, variantId, quantity })
      return res.data
    } catch (err) {
      console.error('Failed to update inventory:', err)
      throw err
    }
  }, [])

  const updateProductFields = useCallback(async (productId, fields) => {
    try {
      const fn = httpsCallable(getFns(), 'shopifyUpdateProductV1')
      const res = await fn({ productId, ...fields })
      return res.data
    } catch (err) {
      console.error('Failed to update product fields:', err)
      throw err
    }
  }, [])

  const updateImage = useCallback(async (productId, imageDataUrl, fileName) => {
    try {
      const fn = httpsCallable(getFns(), 'shopifyUpdateProductImageV1')
      const res = await fn({ productId, imageDataUrl, fileName })
      const data = res?.data || {}
      if (data?.imageUrl) {
        setProducts((prev) => prev.map((item) => (
          item?.id === productId
            ? {
                ...item,
                image: data.imageUrl,
                imageAlt: data.imageAlt || item.imageAlt || null,
                updatedAt: new Date().toISOString()
              }
            : item
        )))
      }
      return data
    } catch (err) {
      console.error('Failed to update product image:', err)
      throw err
    }
  }, [])

  const deleteFromShopify = useCallback(async ({ productId, shopifyId, shopifyGid, cacheDocId } = {}) => {
    try {
      const fn = httpsCallable(getFns(), 'shopifyDeleteProductV1')
      const res = await fn({ productId, shopifyId, shopifyGid, cacheDocId })
      const deletedProductId = String(res?.data?.deletedProductId || '').trim()
      if (deletedProductId) {
        setProducts((prev) => prev.filter((item) => String(item?.id || '') !== deletedProductId))
      }
      return res?.data || { ok: true }
    } catch (err) {
      console.error('Failed to delete product in Shopify:', err)
      throw err
    }
  }, [])

  const searchProducts = useCallback(async (term) => {
    const rawTerm = (term || '').toString().trim()
    const raw = rawTerm.toLowerCase()
    if (!raw) return []
    const digits = raw.replace(/\D/g, '')
    const token = raw.split(/\s+/)[0]

    const resultsMap = new Map()
    const collect = (snap) => {
      snap.docs.forEach(d => {
        resultsMap.set(d.id, { id: d.id, ...d.data() })
      })
    }

    const queries = []

    if (digits.length >= 3) {
      queries.push(
        getDocs(query(collection(db, 'shopify_products'), where('barcode', '==', digits), firestoreLimit(50))),
        getDocs(query(collection(db, 'shopify_products'), where('searchEan8', '==', digits), firestoreLimit(50))),
        getDocs(query(collection(db, 'shopify_products'), where('sku', '==', digits), firestoreLimit(50))),
        getDocs(query(collection(db, 'shopify_products'), where('searchSku', '==', digits), firestoreLimit(50)))
      )
    }

    if (token.length >= 2) {
      queries.push(
        getDocs(query(collection(db, 'shopify_products'), where('searchTokens', 'array-contains', token), firestoreLimit(50))),
        getDocs(query(
          collection(db, 'shopify_products'),
          where('searchTitle', '>=', raw),
          where('searchTitle', '<=', `${raw}\uf8ff`),
          orderBy('searchTitle'),
          firestoreLimit(50)
        ))
      )
    }

    const snapshots = await Promise.allSettled(queries)
    snapshots.forEach((res) => {
      if (res.status === 'fulfilled') {
        collect(res.value)
      }
    })

    let results = Array.from(resultsMap.values())
    if (raw) {
      const qDigits = digits
      results = results.filter(p => {
        const fields = []
        if (p.title) fields.push(String(p.title).toLowerCase())
        if (p.sku) fields.push(String(p.sku).toLowerCase())
        if (p.barcode) fields.push(String(p.barcode).toLowerCase())
        if (p.searchEan8) fields.push(String(p.searchEan8))
        const variants = p.variants || []
        variants.forEach(v => {
          if (v.title) fields.push(String(v.title).toLowerCase())
          if (v.sku) fields.push(String(v.sku).toLowerCase())
          if (v.barcode) fields.push(String(v.barcode).toLowerCase())
        })
        if (fields.some(f => f.includes(raw))) return true
        if (qDigits) {
          return fields.some(f => String(f).replace(/\D/g, '').includes(qDigits))
        }
        return false
      })
    }

    const isLikelySkuSearch = digits.length >= 3 || /^[a-z0-9-]{6,}$/i.test(raw)
    const shouldFallbackToShopify = results.length === 0 && raw.length >= 3 && isLikelySkuSearch
    if (!shouldFallbackToShopify) {
      return results
    }

    const cachedRemote = remoteSearchCacheRef.current.get(raw)
    if (Array.isArray(cachedRemote)) {
      const merged = new Map()
      ;[...results, ...cachedRemote].forEach((item) => {
        if (item?.id) merged.set(item.id, item)
      })
      return Array.from(merged.values())
    }

    try {
      const fn = httpsCallable(getFns(), 'shopifySearchProductsV1')
      const res = await fn({ term: rawTerm, limit: 40 })
      const remoteProducts = Array.isArray(res?.data?.products) ? res.data.products : []
      remoteSearchCacheRef.current.set(raw, remoteProducts)

      const merged = new Map()
      ;[...results, ...remoteProducts].forEach((item) => {
        if (item?.id) merged.set(item.id, item)
      })
      return Array.from(merged.values())
    } catch (err) {
      console.warn('Shopify direct search failed, showing cache-only results:', err)
      return results
    }
  }, [])

  return {
    products,
    loading,
    error,
    syncing,
    lastSync,
    refresh,
    loadMore,
    hasMore,
    syncMore,
    updatePrice,
    ensureBarcode,
    updateInventory,
    updateProductFields,
    updateImage,
    deleteFromShopify,
    searchProducts
  }
}

export default useShopifyProducts
